/**
 * Integración con QZ Tray para impresión ESC/POS de tickets térmicos.
 *
 * Arquitectura:
 *   Backend (AWS) genera bytes ESC/POS (base64)
 *     ↓ HTTP
 *   Frontend (Browser en LAN)
 *     ↓ WebSocket wss://localhost:8181
 *   QZ Tray (corriendo en la PC local)
 *     ↓ TCP raw
 *   Impresora térmica (192.168.x.x:9100)
 *
 * QZ Tray Community es gratuito. En la primera conexión el browser pedirá
 * confirmación del certificado (clic "Permitir" 1 vez por dominio).
 */
import api from "@/lib/api";

let qzModule = null;
let connecting = null;

async function loadQZ() {
  if (qzModule) return qzModule;
  // qz-tray es un módulo ESM; el browser lo trae bajo demanda.
  const mod = await import("qz-tray");
  qzModule = mod.default || mod;
  // Asegurar promesa nativa (qz lo requiere)
  qzModule.api.setPromiseType((resolver) => new Promise(resolver));
  // Modo SHA-256 (default desde 2.1)
  qzModule.api.setSha256Type((data) => {
    // Usamos SubtleCrypto del navegador
    return crypto.subtle.digest("SHA-256", new TextEncoder().encode(data))
      .then((buf) => Array.from(new Uint8Array(buf))
        .map((b) => b.toString(16).padStart(2, "0"))
        .join(""));
  });
  return qzModule;
}

export async function isQZAvailable() {
  try {
    const qz = await loadQZ();
    return qz.websocket.isActive();
  } catch (_) {
    return false;
  }
}

export async function connectQZ() {
  const qz = await loadQZ();
  if (qz.websocket.isActive()) return qz;
  if (connecting) return connecting;
  connecting = (async () => {
    try {
      // Intenta conectar al websocket local de QZ Tray
      // Default: wss://localhost:8181 (secure) o ws://localhost:8182
      await qz.websocket.connect({
        host: "localhost",
        usingSecure: true, // prueba wss primero
        retries: 1,
        delay: 1,
      }).catch(async () => {
        // Fallback a conexión NO segura (para entornos sin certificado QZ aceptado)
        await qz.websocket.connect({
          host: "localhost",
          usingSecure: false,
          retries: 1,
          delay: 1,
        });
      });
      return qz;
    } finally {
      connecting = null;
    }
  })();
  return connecting;
}

export async function disconnectQZ() {
  if (!qzModule) return;
  if (qzModule.websocket.isActive()) {
    try { await qzModule.websocket.disconnect(); } catch (_) { /* noop */ }
  }
}

/**
 * Imprime un ticket ESC/POS via QZ Tray.
 *
 * @param {Object} params
 * @param {string} params.kind   — invoice|credit_note|debit_note|quotation|delivery_note|payment
 * @param {string} params.docId  — id del documento
 */
export async function printTicketViaQZ({ kind, docId }) {
  // 1) Obtener configuración QZ (impresora) y bytes ESC/POS del backend
  const [{ data: cfg }, { data: payload }] = await Promise.all([
    api.get("/print/qz-config"),
    api.get(`/print/escpos/${kind}/${docId}`),
  ]);
  if (!cfg.enabled) {
    throw new Error("QZ Tray no está habilitado. Configúralo en Ajustes → QZ Tray.");
  }

  // 2) Conectar a QZ Tray
  const qz = await connectQZ();

  // 3) Resolver destino de impresión
  //    Si hay printer_name → imprimir por nombre (Windows reconoce la impresora)
  //    Si hay printer_host → imprimir directo a IP:puerto (RAW socket)
  let qzConfig;
  if (cfg.printer_host) {
    qzConfig = qz.configs.create(
      { host: cfg.printer_host, port: cfg.printer_port || 9100 },
      { encoding: cfg.encoding || "CP850" },
    );
  } else if (cfg.printer_name) {
    qzConfig = qz.configs.create(
      cfg.printer_name,
      { encoding: cfg.encoding || "CP850" },
    );
  } else {
    throw new Error("Configura la IP o el nombre de la impresora en Ajustes → QZ Tray.");
  }

  // 4) Enviar bytes ESC/POS (base64) como raw
  await qz.print(qzConfig, [
    {
      type: "raw",
      format: "base64",
      data: payload.data_b64,
    },
  ]);
}

/**
 * Devuelve la lista de impresoras locales detectadas por QZ Tray.
 * Útil para el diálogo de configuración en Ajustes.
 */
export async function listPrinters() {
  const qz = await connectQZ();
  return qz.printers.find();
}
